/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv } from 'mobx-state-tree';
import { replaceArray } from 'util/ArrayOperations';

/**
 * Parse and convert `fieldIDForExpression` to an object `{ fieldID, fieldIDForExpression, tableID }`
 * @param {string} fieldIDForExpression 
 */
export const parseFieldIDForExpression = (fieldIDForExpression) => {
	const length = fieldIDForExpression.length;
	const lastDotPos = fieldIDForExpression.lastIndexOf('.');
	const tableID = fieldIDForExpression.substr(0, lastDotPos);
	const fieldID = fieldIDForExpression.substr(lastDotPos + 1, length);
	return {
		fieldID,
		fieldIDForExpression,
		tableID
	};
};

export const ScopeModel = types
	.model('ScopeModel', {
		fieldId: types.string,
		dataSourceId: types.string,
		sourceModuleId: types.optional(types.string, '')
	})
	.volatile(()=>({
		defaultRecommendations: null,
		scopedFields: null,
		serializedFields: null /*the observable fields is one long string, serialized from sorted and flattened field IDs*/
	}))
	.extend((self) => {
		function fetchPredictRecommendations() {
			return getEnv(self).exploreVM.getSmartsRecommenderWrapper().fetchPredictRecommendations(self.dataSourceId, self.fieldId);
		}

		/*
		 * serialize the scoped fields hierarchical data structure to be a string that is observable.
		 * So that the reaction handler is only trigged when 'net columns' are changed regardless the order
		 */
		const _getSerializedFields = (fields) => {
			if(fields && fields.length){
				return fields.map(field => field.fieldIDForExpression).sort().join(' ');
			} else {
				return '';
			}
		};

		return {
			views: {
				/**
 				* Get default recommendations
 				* @param  {object} field
 				* @returns {Promise} Promise object represents array of default recommendations
 				*/
				getDefaultRecommendations() {
					if (self.defaultRecommendations) {
						return Promise.resolve(self.defaultRecommendations);
					}
					return fetchPredictRecommendations()
						.then(recommendations => {
							self.setDefaultRecommendations(recommendations);
							self.setScopedFields(recommendations);
							const addedFields = getEnv(self).targetModel.getAddedFields();
							if(addedFields && addedFields.length) {
								self.addToScopedFields(addedFields);
							}

							const showRelationshipsMap = getEnv(self).targetModel.getShowRelationships();
							const froms = Object.keys(showRelationshipsMap);
							if (froms.length) {
								froms.forEach((from) => {
									const toEntries = showRelationshipsMap[from];
									self.addToScopedFields(toEntries.map(toEntry => toEntry.to));
								});
							}
							return recommendations;
						});
				},
				getScopedFields() {
					return self.scopedFields;
				}
			},
			actions: {
				/**
				 * Set scoped fields
				 * @param {object[]} fields have data structure like below
					[
						{
							fieldID: '',
							fieldIDForExpression: '',
							tableID: '',
						},
						{
							fieldID: '',
							fieldIDForExpression: '',
							tableID: '',
						}
					]
				* @returns {boolean} `true` if set a new scope `false` if scope does not change
				*/
				setScopedFields(fields) {
					//filter out target
					self.scopedFields = fields && fields.filter( field => field.fieldIDForExpression !== self.fieldId );
					//Serilize the fields into a string that is observable
					const serializedFields = _getSerializedFields(self.scopedFields);

					//if we have a new scope
					if(self.serializedFields !== serializedFields) {
						self.serializedFields = serializedFields;
						return true;
					}
					return false;
				},

				/**
				 * Add To Scope
				 * @param {[string]} addedFields 
				 */
				addToScopedFields(addedFields) {
					if (self.scopedFields) {
						addedFields.forEach((addedField) => {
							const addToScope = parseFieldIDForExpression(addedField);
							if (!self.scopedFields.find(scopedField => scopedField.fieldIDForExpression === addedField)) {
								self.scopedFields.push(addToScope);
							}
						});
					}
					self.serializedFields = _getSerializedFields(self.scopedFields);
				},

				/**
				 * Delete from Scope
				 * @param {[string]} deletedFields 
				 */
				removeFromScopedFields(deletedFields) {
					if (self.scopedFields && self.scopedFields.length) {
						replaceArray(self.scopedFields, self.scopedFields.filter((scopedField => deletedFields.indexOf(scopedField.fieldIDForExpression) === -1)));
					}
					self.serializedFields = _getSerializedFields(self.scopedFields);
				},

				setDefaultRecommendations(recommendations) {
					self.defaultRecommendations = recommendations;
				},

				reset() {
					if(self.scopedFields) {
					//if a scope has been set for current target, then remove the asscioated cache from raw cache 
						const smartsRelationshipStrengthService = getEnv(self).exploreVM.getSmartsRecommenderWrapper().smartsRelationshipStrengthService;
						smartsRelationshipStrengthService.removeDatedEntriesFromCache(self.sourceModuleId, self.fieldId);
					}
					self.setDefaultRecommendations(null);
					self.setScopedFields(null);
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/ScopeModel.js